import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { NeuButton, NeuInput, NeuSelect } from "../../components/neu/Neu";
import { fmtMoney, fmtPct } from "../../lib/format";

const EMPTY = { kind: "advance", percent: "", days: "", notes: "" };
const KINDS = ["advance", "on_delivery", "after_delivery", "retention",
               "guarantee", "maintenance", "warranty", "milestone", "final", "monthly"];
const TIMINGS = ["after_delivery", "on_preparation", "on_order_signed",
                 "after_inspection", "after_invoice"];
const num = (v) => { const n = parseFloat(v); return isFinite(n) ? n : 0; };

// جدول دفعات: توقيت البدء + صفوف (نوع/نسبة/مبلغ محسوب/أيام/ملاحظات) + المجموع.
export const InstallmentsEditor = ({ rows, onChange, head, onHead, grand = 0, currency = "AED" }) => {
  const { t } = useTranslation();
  const set = (index, key) => (e) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, [key]: e.target.value } : row)));
  const timing = head?.payment_start_timing || "after_delivery";
  const totalPct = useMemo(() => rows.reduce((s, r) => s + num(r.percent), 0), [rows]);
  const totalAmt = (totalPct * grand) / 100;
  const okColor = Math.abs(totalPct - 100) < 0.01 ? "text-emerald-600" : "text-destructive";
  const cols = "grid-cols-1 sm:grid-cols-[1.5fr_0.7fr_1fr_0.6fr_1.4fr_auto]";
  return (
    <div className="grid gap-3" data-testid="installments-editor">
      <div className="rounded-2xl bg-blue-50 p-3 dark:bg-blue-900/20">
        <label className="grid gap-2">
          <span className="text-[11px] font-bold text-blue-900 dark:text-blue-100">
            {t("purchases.installments.startTiming")}:
          </span>
          <NeuSelect data-testid="installments-timing" value={timing}
                     onChange={(e) => onHead?.({ ...head, payment_start_timing: e.target.value })}>
            {TIMINGS.map((k) => <option key={k} value={k}>{t(`purchases.installments.timings.${k}`)}</option>)}
          </NeuSelect>
        </label>
      </div>
      <div className={`hidden sm:grid ${cols} gap-2 px-1 text-[10px] font-bold text-muted-foreground`}>
        <span>{t("purchases.installments.kind")}</span>
        <span>{t("purchases.installments.percent")}</span>
        <span>{t("purchases.installments.amount")}</span>
        <span>{t("purchases.installments.days")}</span>
        <span>{t("purchases.installments.notes")}</span><span />
      </div>
      {rows.map((row, index) => (
        <div key={index} className={`grid ${cols} gap-2`} data-testid={`installment-${index}`}>
          <NeuSelect data-testid={`installment-kind-${index}`} value={row.kind || "advance"} onChange={set(index, "kind")}>
            {KINDS.map((k) => <option key={k} value={k}>{t(`purchases.installments.kinds.${k}`)}</option>)}
          </NeuSelect>
          <NeuInput data-testid={`installment-percent-${index}`} dir="ltr" placeholder="0"
                    value={row.percent} onChange={set(index, "percent")} />
          <div dir="ltr" data-testid={`installment-amount-${index}`}
               className="num flex h-12 items-center rounded-2xl bg-muted/40 px-3 text-xs font-bold">
            {currency} {fmtMoney((num(row.percent) * grand) / 100)}
          </div>
          <NeuInput data-testid={`installment-days-${index}`} dir="ltr" placeholder="0"
                    value={row.days || ""} onChange={set(index, "days")} />
          <NeuInput data-testid={`installment-notes-${index}`} placeholder={t("purchases.notesPh")}
                    value={row.notes || ""} onChange={set(index, "notes")} />
          <NeuButton className="px-2" data-testid={`installment-remove-${index}`}
                     onClick={() => onChange(rows.filter((_, i) => i !== index))}>
            <Trash2 className="h-3.5 w-3.5" />
          </NeuButton>
        </div>
      ))}
      <div className={`grid ${cols} items-center gap-2 border-t border-border/50 pt-2 text-xs font-bold`}>
        <span>{t("purchases.installments.total")}</span>
        <span className={`num text-center ${okColor}`}>{fmtPct(totalPct, 1)}%</span>
        <span dir="ltr" className={`num ${okColor}`}>{currency} {fmtMoney(totalAmt)}</span>
        <span /><span /><span />
      </div>
      <NeuButton className="w-full border-2 border-dashed py-3 text-xs" data-testid="installment-add"
                 onClick={() => onChange([...rows, { ...EMPTY }])}>
        <Plus className="h-3.5 w-3.5" /> {t("purchases.installments.add")}
      </NeuButton>
    </div>
  );
};
